import "./WorkflowStep.css";

import { Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from "react";
import { SortColumn } from "react-data-grid";

import { AsteriskButtonIcon, EditGreyIcon, MilestoneFlagIcon, TrashDark } from "../../../../../../../../../assets";
import { WorkflowOrganisationAccessLevel } from "../../../../../../../../../constants";
import { parseProjectPhase, ProjectPhase } from "../../../../../../../../../constants/project-phase";
import { CursorChangeProps, ResultType, WorkflowStepModel } from "../../../../../../../../../models";
import { GetTaskBoardResponse, SearchWorkflowTasksResponse } from "../../../../../../../../../service/query";
import { searchWorkflowTasks } from "../../../../../../../../../service/query/QueryService";
import { ResultData, Status } from "../../../../../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../../../../../service/ValidationErrorFormatter";
import { reorderWorkflowTemplateStep } from "../../../../../../../../../service/workflow";
import { flattenObject } from "../../../../../../../../../utils";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
  ProfileMenuItemType,
  Toast,
} from "../../../../../../../../../widget";
import {
  AvatarCellFormatter,
  DateOnlyWithWarningCellFormatter,
} from "../../../../../../../../../widget/data/DataGrid/components/CellFormatters";
import {
  DateOnlyWithWarningCellFormatterData,
  IconNoActionCellFormatterData,
} from "../../../../../../../../../widget/data/DataGrid/models";
import { TasksBoardContext } from "../../../../TasksBoardContext";
import { getDealStageStartEndDate } from "./utils";

interface UseWorkflowStepProps {
  workflowStep: WorkflowStepModel;
}
interface UseWorkflowStepReturnData {
  showStep: boolean;
  handleShowStep: (show: boolean) => void;

  workflowUuid: string | undefined;
  tasksBoard: GetTaskBoardResponse | undefined;

  stepActionsMenuOptionsList: ProfileMenuItemType[];

  refreshTasksBoard: boolean;
  shouldRefreshTasksBoard: () => void;

  columns: DataGridColumnDefinition[];
  onChange: ({ filtering, paging }: CursorChangeProps) => Promise<ResultType>;
  defaultSortingCriteria: SortColumn[];
  isLoadingTasks: boolean;

  showTaskPanel: boolean;
  setShowTaskPanel: Dispatch<SetStateAction<boolean>>;

  showStepActionsMenu: boolean;
  setShowStepActionsMenu: Dispatch<SetStateAction<boolean>>;

  showDeleteStepModal: boolean;
  setShowDeleteStepModal: Dispatch<SetStateAction<boolean>>;

  showEditStepModal: boolean;
  setShowEditStepModal: Dispatch<SetStateAction<boolean>>;

  task: SearchWorkflowTasksResponse["results"][0] | undefined;
  handleCloseTaskPanel: () => void;

  onStepDrop: (droppedOnStepSequence: number, draggedStepUuid: string) => void;

  isContributorAndCanAddTasks: boolean;
  canEditStep: boolean;
}

const PAGE_SIZE = 7;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
  {
    name: "Task",
    key: "nameLink",
    dataType: "string",
    formatter: "buttonLink",
    minWidth: 300,
    filterable: true,
    sortable: true,
  },
  {
    name: "Assigned to",
    key: "assigneeAvatar",
    dataType: "string",
    formatter: "custom",
    alignment: "center",
    customCellFormatter: AvatarCellFormatter,
    filterable: true,
    minWidth: 110,
  },
  {
    name: "Status",
    key: "status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 94,
    filterable: true,
    sortable: true,
  },
  {
    name: "Deadline",
    key: "customDeadlineDate",
    dataType: "Date",
    formatter: "custom",
    customCellFormatter: DateOnlyWithWarningCellFormatter,
    minWidth: 140,
    filterable: false,
    sortable: true,
  },
  {
    name: "Created at",
    key: "createdAt",
    dataType: "Date",
    formatter: "dateOnly",
    minWidth: 130,
    filterable: false,
    sortable: true,
  },
  {
    name: "Updated at",
    key: "updatedAt",
    dataType: "Date",
    formatter: "dateOnly",
    minWidth: 130,
    filterable: false,
    sortable: true,
  },
  {
    name: "Updated by",
    key: "updatedByUserAvatar",
    dataType: "string",
    formatter: "custom",
    alignment: "center",
    customCellFormatter: AvatarCellFormatter,
    filterable: true,
    minWidth: 110,
  },
  {
    name: "Milestone",
    key: "isMilestoneIcon",
    dataType: "boolean",
    formatter: "iconNoAction",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 90,
  },
  {
    name: "Mandatory",
    key: "isMandatoryIcon",
    dataType: "boolean",
    formatter: "iconNoAction",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 90,
  },
];

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];

export const useWorkflowStep = ({ workflowStep }: UseWorkflowStepProps): UseWorkflowStepReturnData => {
  const {
    hasAccessLevelAndCanEditTemplate,
    canEditStep,
    refreshTasksBoard,
    shouldRefreshTasksBoard,
    projectDates,
    workflowUuid,
    tasksBoard,
    dealDates,
  } = useContext(TasksBoardContext);
  const [showStep, setShowStep] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);

  const [task, setTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [showTaskPanel, setShowTaskPanel] = useState(false);

  const [showDeleteStepModal, setShowDeleteStepModal] = useState(false);
  const [showEditStepModal, setShowEditStepModal] = useState(false);

  const phaseStartEndDates = useMemo(() => {
    return {
      [ProjectPhase.feasibility]: {
        startDate: projectDates?.dates.feasibility?.start,
        endDate: projectDates?.dates.feasibility?.end,
      },
      [ProjectPhase.design]: {
        startDate: projectDates?.dates.design?.start,
        endDate: projectDates?.dates.design?.end,
      },
      [ProjectPhase.implementation]: {
        startDate: projectDates?.dates.implementation?.start,
        endDate: projectDates?.dates.implementation?.end,
      },
      [ProjectPhase.ongoingMonitoring]: {
        startDate: projectDates?.dates.ongoingMonitoring.overallStart,
        endDate: projectDates?.dates.ongoingMonitoring.overallEnd,
      },
    };
  }, [refreshTasksBoard]);

  // For create / update task panel
  const [showStepActionsMenu, setShowStepActionsMenu] = useState(false);

  const stepActionsMenuOptionsList: ProfileMenuItemType[] = [
    {
      id: 1,
      value: "Edit step",
      icon: <EditGreyIcon />,
      action: () => setShowEditStepModal(true),
    },
    {
      id: 2,
      value: "Delete step",
      icon: <TrashDark />,
      action: () => setShowDeleteStepModal(true),
    },
  ];

  const handleCloseTaskPanel = (): void => {
    setTask(undefined);
    setShowTaskPanel(false);
  };

  const handleShowStep = (show: boolean): void => {
    setShowStep(show);
  };

  // eslint-disable-next-line unused-imports/no-unused-vars
  const onStepDrop = async (droppedOnStepSequence: number, draggedStepUuid: string): Promise<void> => {
    if (draggedStepUuid !== workflowStep.uuid) {
      // Call reorderWorkflowTemplateStep here
      const res = await reorderWorkflowTemplateStep({
        workflowStep: {
          sequence: droppedOnStepSequence,
          workflowStepUuid: draggedStepUuid,
        },
        workflowTemplateStep: null,
      });
      if (res.status === Status.Success) {
        // Handle success
        Toast.success({ message: "Step successfully reordered" });
        shouldRefreshTasksBoard();
      }
      if (res.status === Status.Error && res.errors) {
        res.errors.forEach((error) => {
          // Handle error
          Toast.error({ message: getErrorMessageFromCode(error.message) });
        });
      }
    }
  };

  const formatData = useCallback(
    (responseData: SearchWorkflowTasksResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el) as ResultData & { assigneeAvatar: { fullName?: string } } & {
          updatedByUserAvatar: { fullName?: string };
        };

        result.assigneeAvatar = { fullName: el.assignee?.name };
        result.updatedByUserAvatar = { fullName: el.updatedByUser.fullName ?? undefined };

        if (result.isMilestone) {
          result.isMilestoneIcon = {
            icon: <MilestoneFlagIcon className="MilestoneFlagGreyIcon Active" />,
          } as IconNoActionCellFormatterData;
        }
        if (result.isMandatory) {
          result.isMandatoryIcon = {
            icon: <AsteriskButtonIcon className="AsteriskButtonIcon Active" />,
          } as IconNoActionCellFormatterData;
        }
        result.nameLink = {
          text: el.name,
          action: () => {
            setTask(el);
            setShowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        const taskPhaseStartEndDate =
          getDealStageStartEndDate(el.step.phase, dealDates) ??
          phaseStartEndDates[parseProjectPhase(el.step.phase) as ProjectPhase];
        result.customDeadlineDate = {
          date: el.deadlineDate,
          startDate: taskPhaseStartEndDate?.startDate,
          endDate: taskPhaseStartEndDate?.endDate,
          isComplete: el.status === "Complete",
        } as DateOnlyWithWarningCellFormatterData;

        return result;
      }) || [],
    []
  );

  const onChange = async ({ filtering, sorting, paging }: CursorChangeProps): Promise<ResultType> => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: PAGE_SIZE,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (filterCriteria.assigneeAvatar) {
      filterCriteria.assignee = {
        name: filterCriteria.assigneeAvatar,
      };
      delete filterCriteria.assigneeAvatar;
    }

    if (filterCriteria.updatedByUserAvatar) {
      filterCriteria.updatedByUser = {
        fullName: filterCriteria.updatedByUserAvatar,
      };
      delete filterCriteria.updatedByUserAvatar;
    }

    if (filterCriteria.nameLink) {
      filterCriteria.name = filterCriteria.nameLink;
      filterCriteria.nameLink = undefined;
      delete filterCriteria.nameLink;
    }

    filterCriteria.step = {
      uuid: {
        operator: "eq",
        value: workflowStep.uuid,
      },
    };

    const sortKeyMap: Record<string, string> = {
      nameLink: "name",
      assigneeAvatar: "assignee.name",
      updatedByUserAvatar: "updatedByUser.fullName",
      customDeadlineDate: "deadlineDate",
    };

    await searchWorkflowTasks({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: PAGE_SIZE,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: PAGE_SIZE,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setIsLoadingTasks(false);
      });

    return data;
  };

  return {
    showStep,
    handleShowStep,

    workflowUuid,
    tasksBoard,

    stepActionsMenuOptionsList,

    onChange,
    isLoadingTasks,
    columns,
    defaultSortingCriteria,

    showTaskPanel,
    setShowTaskPanel,

    showStepActionsMenu,
    setShowStepActionsMenu,

    showDeleteStepModal,
    setShowDeleteStepModal,

    showEditStepModal,
    setShowEditStepModal,

    task,
    handleCloseTaskPanel,

    onStepDrop,

    refreshTasksBoard,
    shouldRefreshTasksBoard,
    isContributorAndCanAddTasks: hasAccessLevelAndCanEditTemplate(WorkflowOrganisationAccessLevel.Contributor),
    canEditStep: canEditStep(workflowStep.createdByOrganisationUuid),
  };
};
